/**
 * Persistence for "which tours has this user already seen" (item 7.6).
 *
 * WHY localStorage and not the backend: a tour being seen is a purely local,
 * low-stakes UX preference — losing it merely re-offers a tour. Keeping it
 * client-side avoids adding a user-preference endpoint and a write on every
 * dismissal (the task's stated preference). The store is a small JSON array of
 * tour ids under a single namespaced key.
 *
 * Every read is defensive: a malformed or unavailable store (private mode,
 * quota, hand-edited value) degrades to "nothing seen" rather than throwing, so
 * the app never breaks because of a preference blob.
 */

/** The single localStorage key holding the seen-tour ids. */
export const SEEN_TOURS_KEY = 'deployhub.tours.seen';

/** Read the set of seen tour ids. Returns an empty array on any problem. */
export function readSeenTours(): string[] {
  try {
    const raw = window.localStorage.getItem(SEEN_TOURS_KEY);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

/** Whether the given tour has been seen before. */
export function hasSeenTour(id: string): boolean {
  return readSeenTours().includes(id);
}

/** Record that a tour has been seen. Idempotent; swallows storage errors. */
export function markTourSeen(id: string): void {
  try {
    const seen = readSeenTours();
    if (seen.includes(id)) {
      return;
    }
    seen.push(id);
    window.localStorage.setItem(SEEN_TOURS_KEY, JSON.stringify(seen));
  } catch {
    // Persisting a preference is best-effort; never surface a storage failure.
  }
}

/** Forget all seen tours (so they can be offered again). Best-effort. */
export function resetSeenTours(): void {
  try {
    window.localStorage.removeItem(SEEN_TOURS_KEY);
  } catch {
    // Best-effort — see above.
  }
}
